import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import { useTranslate } from "utils/i18n";

export default function MapTimelineBar({
  teacherName,
  teacherReg,
  days,
  selectedDay,
  onSelectDay,
  stats,
  onPrev,
  onNext,
}) {
  const { t } = useTranslate();

  const dayIndex = days.findIndex((d) => d.date === selectedDay);
  const canPrev = selectedDay !== "all" && dayIndex > 0;
  const canNext = selectedDay !== "all" && dayIndex >= 0 && dayIndex < days.length - 1;

  return (
    <div className="map-timeline-bar">
      <div className="map-timeline-header">
        <div className="map-timeline-teacher">
          <Icon sx={{ fontSize: "1.1rem !important", color: "#667eea" }}>person_pin</Icon>
          <div>
            <strong>{teacherName || "—"}</strong>
            {teacherReg ? <span className="map-timeline-reg">{teacherReg}</span> : null}
          </div>
        </div>
        <div className="map-timeline-stats">
          <span>
            <strong>{stats.uniqueDays}</strong> {t("map.timeline.days", "days")}
          </span>
          <span>
            <strong>{stats.checkIns}</strong> {t("map.timeline.check_ins", "check-ins")}
          </span>
          <span className="map-timeline-stat--good">
            <strong>{stats.inRadius}</strong> {t("map.kpi.in_radius", "In radius")}
          </span>
          <span className="map-timeline-stat--warn">
            <strong>{stats.outRadius}</strong> {t("map.kpi.out_radius", "Out of radius")}
          </span>
        </div>
      </div>

      <div className="map-timeline-controls">
        <button
          type="button"
          className="map-timeline-nav"
          onClick={onPrev}
          disabled={!canPrev && selectedDay !== "all"}
          aria-label={t("map.timeline.prev", "Previous day")}
        >
          <Icon sx={{ fontSize: "1rem !important" }}>chevron_left</Icon>
        </button>

        <div className="map-timeline-days">
          <button
            type="button"
            className={`map-timeline-day${selectedDay === "all" ? " is-active" : ""}`}
            onClick={() => onSelectDay("all")}
          >
            {t("map.timeline.all_days", "All")}
            <span className="map-timeline-day-count">{stats.checkIns}</span>
          </button>
          {days.map((day) => (
            <button
              key={day.date}
              type="button"
              className={`map-timeline-day${selectedDay === day.date ? " is-active" : ""}`}
              onClick={() => onSelectDay(day.date)}
              title={day.date}
            >
              {day.date.slice(5)}
              <span className="map-timeline-day-count">{day.count}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="map-timeline-nav"
          onClick={onNext}
          disabled={!canNext && selectedDay === "all"}
          aria-label={t("map.timeline.next", "Next day")}
        >
          <Icon sx={{ fontSize: "1rem !important" }}>chevron_right</Icon>
        </button>
      </div>
    </div>
  );
}

MapTimelineBar.propTypes = {
  teacherName: PropTypes.string,
  teacherReg: PropTypes.string,
  days: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      count: PropTypes.number,
    })
  ).isRequired,
  selectedDay: PropTypes.string.isRequired,
  onSelectDay: PropTypes.func.isRequired,
  stats: PropTypes.shape({
    checkIns: PropTypes.number,
    uniqueDays: PropTypes.number,
    inRadius: PropTypes.number,
    outRadius: PropTypes.number,
  }).isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

MapTimelineBar.defaultProps = {
  teacherName: "",
  teacherReg: "",
};
